import { useEffect, useRef, useState } from "react"
import { ENCOURAGEMENT_QUOTES } from "@/data/encouragementQuotes"
import { Volume2, VolumeX } from "lucide-react"

interface EncouragementSplashProps {
  name?: string
  onDone: () => void
}

// Pick a quote that differs from the last one shown
function pickQuote(): string {
  const lastIdx = parseInt(localStorage.getItem("lastQuoteIdx") ?? "-1")
  let idx: number
  do {
    idx = Math.floor(Math.random() * ENCOURAGEMENT_QUOTES.length)
  } while (idx === lastIdx && ENCOURAGEMENT_QUOTES.length > 1)
  localStorage.setItem("lastQuoteIdx", String(idx))
  return ENCOURAGEMENT_QUOTES[idx]
}

const DISPLAY_MS = 9000 // total duration before auto-dismiss

export function EncouragementSplash({ name, onDone }: EncouragementSplashProps) {
  const [quote]     = useState<string>(pickQuote)
  const [phase, setPhase]   = useState<"in" | "hold" | "out">("in")
  const [muted, setMuted]   = useState(true)
  const [progress, setProgress] = useState(0)
  const utterRef = useRef<SpeechSynthesisUtterance | null>(null)
  const rafRef   = useRef<number>(0)
  const startRef = useRef<number>(0)

  // TTS — only fires when user has opted in (muted=false)
  useEffect(() => {
    if (!window.speechSynthesis) return
    window.speechSynthesis.cancel()
    if (muted) return

    const speak = () => {
      const utter = new SpeechSynthesisUtterance(quote)
      utter.rate  = 0.88
      utter.pitch = 1.05
      const voices = window.speechSynthesis.getVoices()
      const natural = voices.find(
        (v) =>
          v.lang.startsWith("en") &&
          !/(espeak|mbrola|festival|flite|pico)/i.test(v.name)
      )
      if (natural) utter.voice = natural
      utterRef.current = utter
      window.speechSynthesis.speak(utter)
    }

    // Voices may not be loaded yet on first call
    if (window.speechSynthesis.getVoices().length > 0) {
      speak()
    } else {
      window.speechSynthesis.addEventListener("voiceschanged", speak, { once: true })
    }

    return () => window.speechSynthesis.cancel()
  }, [quote, muted])

  // Progress bar + auto-dismiss
  useEffect(() => {
    startRef.current = performance.now()
    const tick = (now: number) => {
      const elapsed = now - startRef.current
      const pct = Math.min(elapsed / DISPLAY_MS, 1)
      setProgress(pct)
      if (pct < 1) {
        rafRef.current = requestAnimationFrame(tick)
      } else {
        handleDismiss()
      }
    }
    rafRef.current = requestAnimationFrame(tick)
    return () => cancelAnimationFrame(rafRef.current)
  }, [])

  const handleDismiss = () => {
    if (phase === "out") return
    window.speechSynthesis?.cancel()
    setPhase("out")
    setTimeout(onDone, 500)
  }

  const toggleMute = (e: React.MouseEvent) => {
    e.stopPropagation()
    if (!muted) {
      window.speechSynthesis?.cancel()
    }
    setMuted((m) => !m)
  }

  // Word-by-word reveal — each word fades up with staggered delay
  const words = quote.split(" ")

  return (
    <div
      onClick={handleDismiss}
      className="fixed inset-0 z-[9999] flex flex-col items-center justify-center cursor-pointer select-none overflow-hidden"
      style={{
        background: "linear-gradient(135deg, #3730a3 0%, #6d28d9 55%, #9333ea 100%)",
        opacity: phase === "out" ? 0 : 1,
        transition: phase === "out" ? "opacity 500ms ease" : "opacity 300ms ease",
      }}
    >
      {/* Soft radial glow */}
      <div
        className="absolute inset-0 pointer-events-none"
        style={{
          background: "radial-gradient(ellipse 70% 50% at 50% 40%, rgba(255,255,255,0.07) 0%, transparent 70%)",
        }}
      />

      {/* Sound toggle — off by default, tap to enable */}
      <button
        onClick={toggleMute}
        className="absolute top-5 right-5 flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-white/15 hover:bg-white/25 transition-colors"
      >
        {muted
          ? <><VolumeX className="w-3.5 h-3.5 text-white/60" /><span className="text-white/60 text-[11px] font-semibold">Sound off</span></>
          : <><Volume2 className="w-3.5 h-3.5 text-white/90" /><span className="text-white/90 text-[11px] font-semibold">Sound on</span></>
        }
      </button>

      {/* Logo mark */}
      <div
        className="flex flex-col items-center gap-2 mb-10"
        style={{
          opacity: phase === "in" ? 0 : 1,
          transform: phase === "in" ? "translateY(-12px)" : "translateY(0)",
          transition: "opacity 600ms ease 100ms, transform 600ms ease 100ms",
        }}
      >
        <div className="w-12 h-12 rounded-2xl bg-white/15 border border-white/20 flex items-center justify-center">
          <svg viewBox="0 0 40 40" className="w-6 h-6" fill="none">
            <path d="M12 26 C12 18 20 12 28 14" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            <path d="M28 14 C30 22 24 30 16 30" stroke="white" strokeWidth="2.5" strokeLinecap="round" />
            <circle cx="20" cy="20" r="3" fill="white" />
          </svg>
        </div>
        <span className="text-white/60 text-[11px] font-bold tracking-[0.25em] uppercase">EarlyScape</span>
      </div>

      {/* Greeting */}
      {name && (
        <p
          className="text-white/70 text-lg font-semibold mb-5 tracking-wide"
          style={{
            opacity: phase === "in" ? 0 : 1,
            transition: "opacity 600ms ease 250ms",
          }}
        >
          Hi {name}
        </p>
      )}

      {/* Quote — word by word */}
      <div className="max-w-sm px-8 text-center">
        <p className="text-white text-2xl sm:text-3xl font-black leading-snug">
          {words.map((word, i) => (
            <span
              key={i}
              className="inline-block"
              style={{
                opacity: 0,
                transform: "translateY(10px)",
                animation: `wordIn 0.45s ease forwards`,
                animationDelay: `${0.35 + i * 0.07}s`,
                marginRight: "0.28em",
              }}
            >
              {word}
            </span>
          ))}
        </p>
      </div>

      {/* Skip button — always visible */}
      <button
        onClick={handleDismiss}
        className="mt-10 px-5 py-2 rounded-full bg-white/15 hover:bg-white/25 text-white/80 hover:text-white text-xs font-semibold tracking-wide transition-all"
      >
        Skip
      </button>

      {/* Progress bar */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-white/10">
        <div
          className="h-full bg-white/50 transition-none"
          style={{ width: `${progress * 100}%` }}
        />
      </div>

      {/* Keyframe injection */}
      <style>{`
        @keyframes wordIn {
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  )
}
